import { useState, useEffect } from 'react'
import { App, Typography, theme, Flex, Alert } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { AuthService } from '../services/auth/authService'
import { ProfileService } from '../services/profile/profileService'
import { ReferralService } from '../services/referral/referralService'
import {
  LoginForm,
  EmailConfirmation,
  ForgotPasswordForm,
  ResetPasswordForm,
} from '../components/Auth'
import { SignUpFlow, type SignUpFlowData } from '../components/Auth/SignUpFlow'
import { useAuthContext } from '../context/auth'
import { supabase } from '../utils/supabase'
import {
  PageContainer,
  CardContainer,
  CenteredTitle,
  AlertDescription,
  AlertDescriptionNoHyphens,
  FlexWithMargin,
  FullWidthButton,
  CompactLinkButton,
  LogoLink,
} from './styles/LoginPageStyles'

const { Text } = Typography
const { useToken } = theme

// Helper to parse and map Supabase error codes to user-friendly messages
function parseEmailConfirmationError(
  hash: string
): { message: string; description: string } | null {
  const errorMatch = hash.match(/error=([^&]+)/)
  const errorCodeMatch = hash.match(/error_code=([^&]+)/)
  const errorDescriptionMatch = hash.match(/error_description=([^&]+)/)

  if (!errorMatch && !errorCodeMatch) {
    return null
  }

  const error = errorMatch ? decodeURIComponent(errorMatch[1]) : null
  const errorCode = errorCodeMatch ? decodeURIComponent(errorCodeMatch[1]) : null
  const errorDescription = errorDescriptionMatch
    ? decodeURIComponent(errorDescriptionMatch[1].replace(/\+/g, ' '))
    : null

  const errorMessages: Record<string, { message: string; description: string }> = {
    otp_expired: {
      message: 'Confirmation Link Expired',
      description:
        'This email confirmation link has expired. Please request a new confirmation email or try signing up again.',
    },
    email_not_confirmed: {
      message: 'Email Not Confirmed',
      description:
        'Your email address has not been confirmed yet. Please check your inbox for a confirmation email.',
    },
    invalid_token: {
      message: 'Invalid Link',
      description: 'This confirmation link is invalid. Please request a new confirmation email.',
    },
  }

  if (errorCode && errorCode in errorMessages) {
    return errorMessages[errorCode]
  }

  if (error === 'access_denied') {
    return {
      message: 'Access Denied',
      description:
        errorDescription ?? 'You do not have permission to access this confirmation link.',
    }
  }

  return {
    message: 'Email confirmation failed',
    description: errorDescription ?? 'The confirmation link is invalid or has expired.',
  }
}

// Check if URL contains email confirmation tokens
function hasEmailConfirmationTokens(hash: string, searchParams: URLSearchParams): boolean {
  return (
    (hash.includes('access_token') && hash.includes('type=email')) ||
    searchParams.get('type') === 'email'
  )
}

// SessionStorage keys for tracking confirmation state
const STORAGE_KEYS = {
  CONFIRMATION_PENDING: 'email_confirmation_pending',
  CONFIRMATION_SUCCESS: 'email_confirmation_success',
} as const

export function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, loading: authLoading } = useAuthContext()
  // Start in signup mode if accessed via /signup route
  const [isSignUp, setIsSignUp] = useState(location.pathname === '/signup')
  const [loading, setLoading] = useState(false)
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false)
  const [emailConfirmed, setEmailConfirmed] = useState(false)
  const [emailError, setEmailError] = useState<{ message: string; description: string } | null>(
    null
  )
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [resetEmailSent, setResetEmailSent] = useState(false)
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false)
  const { token } = useToken()
  const { message, notification } = App.useApp()

  // Redirect already-authenticated users to dashboard
  useEffect(() => {
    if (!authLoading && user) {
      void navigate('/')
    }
  }, [authLoading, user, navigate])

  // Clear auth redirect flag when landing on login page
  useEffect(() => {
    sessionStorage.removeItem('authRedirecting')
  }, [])

  // Check for referral code in URL on mount and store it
  useEffect(() => {
    ReferralService.checkAndStoreReferralFromUrl()
  }, [])

  // Check for recovery mode on mount and listen for auth events
  useEffect(() => {
    const hash = window.location.hash

    // Immediately check if URL contains recovery tokens
    if (hash.includes('type=recovery')) {
      sessionStorage.setItem('password_recovery_mode', 'true')
      setShowResetPassword(true)
      // Clean up URL after a short delay to let Supabase process
      setTimeout(() => {
        window.history.replaceState({}, '', '/login')
      }, 100)
    }

    // Check if we're already in recovery mode
    const isRecoveryMode = sessionStorage.getItem('password_recovery_mode') === 'true'
    if (isRecoveryMode) {
      setShowResetPassword(true)
    }

    // Also listen for PASSWORD_RECOVERY event as backup
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        sessionStorage.setItem('password_recovery_mode', 'true')
        setShowResetPassword(true)
        setShowForgotPassword(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Handle URL tokens for email confirmation
  useEffect(() => {
    const hash = location.hash
    const searchParams = new URLSearchParams(location.search)

    // Skip if already handling recovery
    if (hash.includes('type=recovery')) {
      return undefined
    }

    // Handle error cases
    const parsedError = parseEmailConfirmationError(hash)
    if (parsedError) {
      setEmailError(parsedError)
      window.history.replaceState({}, '', '/login')
      return undefined
    }

    // Handle email confirmation tokens
    if (hasEmailConfirmationTokens(hash, searchParams)) {
      sessionStorage.setItem(STORAGE_KEYS.CONFIRMATION_PENDING, 'true')

      // Listen for Supabase to process the confirmation
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (['SIGNED_IN', 'TOKEN_REFRESHED', 'USER_UPDATED'].includes(event)) {
          if (session?.user.email_confirmed_at) {
            // Show success message
            setEmailConfirmed(true)
            setEmailError(null)
            sessionStorage.setItem(STORAGE_KEYS.CONFIRMATION_SUCCESS, 'true')
            sessionStorage.removeItem(STORAGE_KEYS.CONFIRMATION_PENDING)
            window.history.replaceState({}, '', '/login')

            // Sign user out so they need to log in manually
            await supabase.auth.signOut()

            subscription.unsubscribe()
          }
        }
      })

      return () => subscription.unsubscribe()
    }

    // Handle case where confirmation was already processed (page refresh or fast redirect)
    const wasSuccessful = sessionStorage.getItem(STORAGE_KEYS.CONFIRMATION_SUCCESS) === 'true'
    if (wasSuccessful) {
      void supabase.auth.getSession().then(async ({ data: { session } }) => {
        if (session?.user.email_confirmed_at) {
          setEmailConfirmed(true)
          // Sign out to ensure user must log in manually
          await supabase.auth.signOut()
          sessionStorage.removeItem(STORAGE_KEYS.CONFIRMATION_SUCCESS)
        } else {
          sessionStorage.removeItem(STORAGE_KEYS.CONFIRMATION_SUCCESS)
        }
      })
    }
    return undefined
  }, [location.hash, location.search])

  // Sync with auth context when user becomes confirmed (handles race conditions)
  useEffect(() => {
    if (!user?.email_confirmed_at || emailConfirmed || emailError) {
      return
    }

    const hasConfirmationFlag =
      sessionStorage.getItem(STORAGE_KEYS.CONFIRMATION_PENDING) === 'true' ||
      sessionStorage.getItem(STORAGE_KEYS.CONFIRMATION_SUCCESS) === 'true'

    if (hasConfirmationFlag) {
      setEmailConfirmed(true)
      // Sign out to ensure user must log in manually
      void supabase.auth.signOut()
      sessionStorage.removeItem(STORAGE_KEYS.CONFIRMATION_PENDING)
      sessionStorage.removeItem(STORAGE_KEYS.CONFIRMATION_SUCCESS)
    }
  }, [user, emailConfirmed, emailError])

  const handleLogin = async (values: { email: string; password: string }) => {
    setLoading(true)
    try {
      const { error } = await AuthService.signIn(values.email, values.password)
      if (error) {
        notification.error({
          title: 'Login Failed',
          description: error.message,
          placement: 'top',
        })
      } else {
        message.success('Login successful')
        // Check user role to determine redirect destination
        const profileResult = await ProfileService.get()
        const profile = profileResult.data

        // Attempt to attribute referral for regular users (not partners/admins)
        // This runs in the background and doesn't block navigation
        if (profile?.role === 'user') {
          ReferralService.attributeReferral().catch(() => {
            // Silently ignore attribution errors - not critical for user experience
          })
        }

        if (profile?.role === 'partner') {
          // Redirect based on partner approval status
          if (profile.partner_status === 'approved') {
            void navigate('/partner')
          } else {
            void navigate('/partner/pending')
          }
        } else if (profile?.role === 'admin' || profile?.role === 'super_admin') {
          void navigate('/admin')
        } else {
          void navigate('/')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSignUp = async (values: SignUpFlowData) => {
    setLoading(true)
    try {
      const { error } = await AuthService.signUp(
        values.email,
        values.password,
        values.fullName,
        values.termsAccepted,
        values.newsletterOptIn,
        {
          surveyUserRole: values.surveyUserRole,
          surveyIdeationFrequency: values.surveyProjectStage,
          surveyUseCases: values.surveySessionGoals,
        }
      )
      if (error) {
        notification.error({
          title: 'Registration Failed',
          description: error.message,
          placement: 'top',
        })
      } else {
        message.success('Account created! Check your email to confirm your address.')
        const profileResult = await ProfileService.get()
        const profile = profileResult.data
        if (profile?.role === 'partner') {
          void navigate(profile.partner_status === 'approved' ? '/partner' : '/partner/pending')
        } else if (profile?.role === 'admin' || profile?.role === 'super_admin') {
          void navigate('/admin')
        } else {
          void navigate('/')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const handleBackToLogin = () => {
    sessionStorage.removeItem('password_recovery_mode')
    setShowEmailConfirmation(false)
    setShowForgotPassword(false)
    setShowResetPassword(false)
    setResetEmailSent(false)
    setIsSignUp(false)
  }

  const handleForgotPassword = async (values: { email: string }) => {
    setLoading(true)
    try {
      const { error } = await AuthService.resetPassword(values.email)
      if (error) {
        notification.error({
          title: 'Error',
          description: error.message,
          placement: 'top',
        })
      } else {
        setResetEmailSent(true)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (values: { password: string }) => {
    setLoading(true)
    try {
      const { error } = await AuthService.updatePassword(values.password)
      if (error) {
        notification.error({
          title: 'Password Reset Failed',
          description: error.message,
          placement: 'top',
        })
      } else {
        sessionStorage.removeItem('password_recovery_mode')
        setShowResetPassword(false)
        setPasswordResetSuccess(true)
        message.success('Password updated successfully')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageContainer
      align="center"
      justify="center"
      $colorPrimary={token.colorPrimary}
      $colorBgContainer={token.colorBgContainer}
    >
      <LogoLink href="https://bandos.ai" $color={token.colorText}>
        Bandos
      </LogoLink>
      <CardContainer
        $colorBgContainer={token.colorBgContainer}
        $borderRadius={token.borderRadius}
        $boxShadowTertiary={token.boxShadowTertiary}
      >
        {showEmailConfirmation ? (
          <EmailConfirmation onBackToLogin={handleBackToLogin} />
        ) : showResetPassword ? (
          <Flex vertical gap="large">
            <CenteredTitle level={2}>Reset Password</CenteredTitle>
            <ResetPasswordForm onFinish={handleResetPassword} loading={loading} />
          </Flex>
        ) : showForgotPassword ? (
          <Flex vertical gap="large">
            <CenteredTitle level={2}>Forgot Password</CenteredTitle>
            <ForgotPasswordForm
              onFinish={handleForgotPassword}
              onBack={handleBackToLogin}
              loading={loading}
              emailSent={resetEmailSent}
            />
          </Flex>
        ) : (
          <Flex vertical gap="large">
            <CenteredTitle level={2}>{isSignUp ? 'Create Account' : 'Welcome Back'}</CenteredTitle>

            {passwordResetSuccess && (
              <Alert
                message="Password Updated"
                description="Your password has been reset successfully. Please log in with your new password."
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                closable
                onClose={() => setPasswordResetSuccess(false)}
              />
            )}

            {emailConfirmed && (
              <Alert
                message="Email Confirmed!"
                description={
                  <AlertDescription>
                    Your email has been successfully confirmed. You can now log in with your
                    credentials.
                  </AlertDescription>
                }
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                closable
                onClose={() => setEmailConfirmed(false)}
              />
            )}

            {emailError && (
              <FlexWithMargin vertical gap="small">
                <Alert
                  message={emailError.message}
                  description={
                    <AlertDescriptionNoHyphens>{emailError.description}</AlertDescriptionNoHyphens>
                  }
                  type="error"
                  showIcon
                  icon={<ExclamationCircleOutlined />}
                  closable
                  onClose={() => setEmailError(null)}
                />
                <FullWidthButton
                  type="primary"
                  onClick={() => {
                    setEmailError(null)
                    setIsSignUp(true)
                  }}
                >
                  Sign Up Again
                </FullWidthButton>
              </FlexWithMargin>
            )}

            {isSignUp ? (
              <SignUpFlow onFinish={handleSignUp} loading={loading} />
            ) : (
              <LoginForm
                onFinish={handleLogin}
                onForgotPassword={() => setShowForgotPassword(true)}
                loading={loading}
              />
            )}

            <Flex justify="center" gap="small">
              <Text type="secondary">
                {isSignUp ? 'Already have an account?' : "Don't have an account?"}
              </Text>
              <CompactLinkButton type="link" onClick={() => setIsSignUp(!isSignUp)}>
                {isSignUp ? 'Sign In' : 'Sign Up'}
              </CompactLinkButton>
            </Flex>
          </Flex>
        )}
      </CardContainer>
    </PageContainer>
  )
}
